import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useAppDispatch } from 'store/hooks';
import { Spinner2 } from 'components/Spinner2';
import { useTranslation } from 'react-i18next';
import { customerInitialState } from 'slices/dealer-customers';
import { FormikHelpers } from 'formik';
import i18n from 'translations';
import { IDealerInfo } from 'slices/auth';
import {
  useAddNewCustomerMutation,
  useGetOneCustomerQuery,
  useUpdateCustomerMutation,
  dealerCustomerApi,
} from 'thunks/RTKQuery/dealerCustomer';
import { handleRTKResponseToaster } from 'utils/handle-api-response';
import { useGetDealerListQuery } from 'thunks/RTKQuery/dealer';
import { useDealerRole } from 'hooks/role';
import store from 'store/store';
import {
  ICustomerItem,
  ICustomerObject,
  IValidationCustomer,
  IDealerOption,
  IDealerDropDownView,
} from '../Models';
import { DealerCustomersDetailView } from './component';

interface ICustomerDetailContainer {
  setErrorString: (err: string) => void;
  selectedCustomer?: ICustomerItem;
  rootDealer?: IDealerInfo;
  setSelectedCustomer: (dealer: ICustomerItem) => void;
  registrationDbData?: ICustomerObject;
  showExtendedButton?: boolean;
  callback?: (customer: ICustomerItem, saveAndSelect: boolean) => void;
}

const schema: yup.SchemaOf<IValidationCustomer> = yup.object().shape({
  dealerInfo: yup.object().shape({
    dealerId: yup
      .string()
      .test(
        'required',
        i18n.t('dealerManagement.requiredDealerId'),
        (value) => {
          return (
            !!value ||
            new yup.ValidationError(
              i18n.t('dealerManagement.requiredDealerId'),
              value,
              'dealerId'
            )
          );
        }
      ),
  }),
  customerInfo: yup.object().shape({
    customerId: yup.string().when('id', {
      is: (val: number | undefined) => val === 0 || val === undefined,
      then: yup
        .string()
        .test(
          'isUniqueCustomerId',
          i18n.t('customerManagement.uniqueCustomerId'),
          async (value) => {
            if (!value) return true; // Skip validation if value is empty
            try {
              const actionResult = await store.dispatch(
                dealerCustomerApi.endpoints.checkCustomerExists.initiate(value)
              );
              const result = actionResult.data;
              if (result?.customerExists) {
                return new yup.ValidationError(
                  i18n.t('customerManagement.uniqueCustomerId'),
                  value,
                  'customerId'
                );
              }
            } catch (error) {
              return new yup.ValidationError(
                i18n.t('customerManagement.uniqueCustomerId'),
                value,
                'customerId'
              );
            }
            return true;
          }
        ),
    }),
    firstName: yup
      .string()
      .test(
        'requiredFirstName',
        i18n.t('customerManagement.requiredFirstName'),
        (value) => {
          return (
            !!value ||
            new yup.ValidationError(
              i18n.t('customerManagement.requiredFirstName'),
              value,
              'firstName'
            )
          );
        }
      ),
    lastName: yup
      .string()
      .test(
        'requiredLastName',
        i18n.t('customerManagement.requiredLastName'),
        (value) => {
          return (
            !!value ||
            new yup.ValidationError(
              i18n.t('customerManagement.requiredLastName'),
              value,
              'lastName'
            )
          );
        }
      ),
    company: yup
      .string()
      .test(
        'requiredCompany',
        i18n.t('customerManagement.requiredCompany'),
        (value) => {
          return (
            !!value ||
            new yup.ValidationError(
              i18n.t('customerManagement.requiredCompany'),
              value,
              'company'
            )
          );
        }
      ),
  }),
});

export const DealerCustomersDetailContainer = ({
  setErrorString,
  selectedCustomer,
  setSelectedCustomer,
  rootDealer,
  registrationDbData,
  showExtendedButton,
  callback,
}: ICustomerDetailContainer) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [customerData, setCustomerData] = useState<ICustomerObject>(
    customerInitialState.data
  );
  const [saveAndSelect, setSaveAndSelect] = useState<boolean>(false);
  const isDealer = useDealerRole();
  const [dealerOptions, setDealerOptions] = React.useState<IDealerOption[]>([]);

  const params = 'id,name,dealerId';
  const {
    data: dealerList = [],
    isLoading: isdealerDataLoading,
    error: dealerDataError,
  } = useGetDealerListQuery(
    !isDealer ? params : `${params}&dealerId=${rootDealer?.dealerId}`
  );

  useEffect(() => {
    if (!Array.isArray(dealerList) || !dealerList.length) return;

    const options: IDealerOption[] = [];
    dealerList.forEach((subDealer: IDealerDropDownView) => {
      options.push({
        label: `${subDealer.dealerId} (${subDealer.name})`,
        value: subDealer.dealerId,
      });
    });
    setDealerOptions(options);
  }, [dealerList]);

  useEffect(() => {
    if (dealerDataError && 'message' in dealerDataError) {
      setErrorString(`${dealerDataError.message}`);
    }
  }, [dealerDataError]);

  useEffect(() => {
    setCustomerData({
      ...customerData,
      dealerInfo: {
        ...customerData.dealerInfo,
        dealerId: rootDealer?.dealerId ?? '',
      },
    });
  }, [rootDealer]);

  const {
    data: detailData,
    isLoading: customerDetailLoading,
    error: customerDetailError,
    refetch: refetchDealerDetail,
  } = useGetOneCustomerQuery(selectedCustomer?.dealerCustomerId ?? 0, {
    skip: !selectedCustomer?.dealerCustomerId,
  });

  useEffect(() => {
    if (!detailData || !detailData?.dealerCustomerId) return;
    setCustomerData(detailData);
  }, [detailData]);

  useEffect(() => {
    if (!selectedCustomer) {
      setCustomerData({
        ...customerInitialState.data,
        dealerInfo: {
          ...customerInitialState.data.dealerInfo,
          dealerId: rootDealer?.dealerId ?? '',
        },
      });
    }
    refetchDealerDetail();
  }, [selectedCustomer]);

  const [
    addNewCustomer,
    {
      isLoading: customerSaveLoading,
      error: customerSaveError,
      status: customerSaveStatus,
    },
  ] = useAddNewCustomerMutation();
  const [
    updateCustomer,
    {
      isLoading: customerUpdateLoading,
      error: customerUpdateError,
      status: customerUpdateStatus,
    },
  ] = useUpdateCustomerMutation();

  useEffect(() => {
    if (customerDetailError && 'message' in customerDetailError) {
      setErrorString(`${customerDetailError.message}`);
    }
  }, [customerDetailError]);

  const handleSubmit = async (
    values: ICustomerObject,
    formik: FormikHelpers<ICustomerObject>
  ) => {
    setErrorString('');
    let res: any;
    if (!selectedCustomer?.dealerCustomerId) {
      res = await addNewCustomer(values);
      dispatch(
        dealerCustomerApi.util.invalidateTags(['Customers', 'OneCustomer'])
      );
    } else {
      res = await updateCustomer(values);
    }
    if (!res?.error) {
      const dealerObj = res.data as ICustomerItem;
      const resultItem = {
        dealerCustomerId: dealerObj.dealerCustomerId,
        customerInfo: {
          firstName: values.customerInfo.firstName,
          lastName: values.customerInfo.lastName,
          company: values.customerInfo.company,
        },
        dealerInfo: {
          id: values.dealerInfo.id,
          dealerId: values.dealerInfo.dealerId,
          dealerName: values.dealerInfo.dealerName,
        },
      } as ICustomerItem;
      setSelectedCustomer(resultItem);
      if (callback) {
        callback(resultItem, saveAndSelect);
      }
    }

    formik.resetForm({ values: { ...values } });
  };

  useEffect(() => {
    handleRTKResponseToaster(
      customerSaveStatus,
      customerSaveError,
      dispatch,
      t('contract.error'),
      t('dealer.customer.title'),
      t('dealer.customer.message')
    );
  }, [customerSaveStatus]);

  useEffect(() => {
    handleRTKResponseToaster(
      customerUpdateStatus,
      customerUpdateError,
      dispatch,
      t('contract.error'),
      t('dealer.customer.title'),
      t('dealer.customer.message')
    );
  }, [customerUpdateStatus]);

  return (
    <>
      <Spinner2
        show={
          customerDetailLoading ||
          customerUpdateLoading ||
          customerSaveLoading ||
          isdealerDataLoading
        }
      />
      <DealerCustomersDetailView
        detailData={registrationDbData || customerData}
        validationSchema={schema}
        onSubmit={handleSubmit}
        rootDealer={rootDealer}
        dealerOptions={dealerOptions}
        showExtendedButton={showExtendedButton}
        setSaveAndSelect={setSaveAndSelect}
      />
    </>
  );
};

DealerCustomersDetailContainer.defaultProps = {
  selectedCustomer: undefined,
  rootDealer: undefined,
  registrationDbData: undefined,
  showExtendedButton: false,
  callback: undefined,
};
